import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/api';

const getInitials = (name) => name?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';

const avatarColors = ['#6366f1', '#8b5cf6', '#ec4899', '#14b8a6', '#f59e0b', '#ef4444'];
const getColor = (str) => avatarColors[str?.charCodeAt(0) % avatarColors.length] || '#6366f1';

const ClientList = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => { fetchClients(); }, []);

  const fetchClients = async () => {
    try {
      const res = await apiClient.get('/clients');
      setClients(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="clients-loading">
        <div className="w-8 h-8 rounded-full border-2 border-indigo-500 border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="client-list">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="client-list-title">Clients</h1>
          <p className="text-slate-500 text-sm mt-1">{clients.length} client{clients.length !== 1 ? 's' : ''} total</p>
        </div>
        <button onClick={() => navigate('/clients/new')}
          className="btn-primary flex items-center gap-2"
          data-testid="create-client-button">
          <span>+</span> Add Client
        </button>
      </div>

      {clients.length === 0 ? (
        <div className="card p-16 text-center" data-testid="empty-clients">
          <div className="text-5xl mb-4">👥</div>
          <h3 className="text-xl font-bold text-slate-900 mb-2">No clients yet</h3>
          <p className="text-slate-500 mb-6">Add your first client to start sending invoices</p>
          <button onClick={() => navigate('/clients/new')}
            className="btn-primary"
            data-testid="create-first-client-button">
            Add Client
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {clients.map(client => (
            <div key={client.id}
              className="card p-5 hover:shadow-md transition-all cursor-pointer group"
              data-testid={`client-card-${client.id}`}
              onClick={() => navigate(`/clients/edit/${client.id}`)}
              style={{ borderLeft: `3px solid ${getColor(client.name)}` }}>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                  style={{ background: `linear-gradient(135deg, ${getColor(client.name)}, ${getColor(client.name)}cc)` }}>
                  {getInitials(client.name)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-slate-900 truncate group-hover:text-indigo-600 transition-colors">
                      {client.name}
                    </h3>
                    {!client.is_active && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600 ml-2">Inactive</span>
                    )}
                  </div>
                  {client.company && (
                    <p className="text-sm text-slate-500 mt-0.5 truncate">{client.company}</p>
                  )}
                  <p className="text-sm text-slate-500 mt-1 truncate">{client.email}</p>
                  {client.phone && <p className="text-xs text-slate-400 mt-0.5 truncate">{client.phone}</p>}
                </div>
              </div>
              {client.address && (
                <p className="text-xs text-slate-400 mt-3 pt-3 border-t border-slate-100 truncate">{client.address}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClientList;